/**
 * =========================================================
 * professional/api.ts — Clinician Dashboard Hook
 * =========================================================
 * Çfarë bën: Thirrja GET /api/v1/clinician/home/summary dhe
 *            hook useClinicianSummary me:
 *            - Page Visibility API: polling ndalon kur tab i mbyllur
 *            - Adaptive Polling: 30s → 60s nëse data nuk ndryshon 3×
 * Endpoint: GET /api/v1/clinician/home/summary
 * =========================================================
 */

import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/lib/api/client";

// =========================================================
// [TIPET] — reflektojnë ClinicianHomeSummaryOut nga backend
// =========================================================
export interface TodayStats {
  total: number; confirmed: number; requested: number; completed: number;
}
export interface TodayAppointment {
  id: number; patient_label: string;
  start_at: string; end_at: string; status: string;
}
export interface NextAppointment {
  id: number; patient_label: string;
  start_at: string; end_at: string; status: string; minutes_until: number;
}
export interface PendingActions {
  draft_reports: number; unread_messages: number; requested_appointments: number;
}
export interface EarningsGraphPoint { date: string; amount: number; }
export interface ClinicianHomeSummary {
  clinician_id:     number;
  generated_at:     string;
  today_stats:      TodayStats;
  today_schedule:   TodayAppointment[];
  next_appointment: NextAppointment | null;
  pending_actions:  PendingActions;
  earnings: {
    balance: string; currency: string; last_7_days: string;
    last_tx: { amount: string; description: string | null; created_at: string }[];
  };
  earnings_graph: EarningsGraphPoint[];
  my_contributions: BuilderReportDashboardItem[];
  continue_working: BuilderReportDashboardItem[];
}

export interface BuilderReportDashboardItem {
  report_id: number;
  template_name: string;
  patient_id: number;
  doctor_id: number;
  session_id?: string | null;
  status: string;
  created_at: string;
  updated_at?: string | null;
}

export interface DoctorWorkspacePatient {
  account_id: number;
  patient_id: number;
  display_name: string;
  email?: string | null;
  phone?: string | null;
  date_of_birth?: string | null;
  gender?: string | null;
  address?: string | null;
  id_number?: string | null;
  source: string;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface DoctorWorkspaceReport {
  report_id: number;
  template_id: number;
  template_name: string;
  patient_account_id: number;
  patient_name: string;
  status: string;
  session_id?: string | null;
  episode_id?: number | null;
  created_at: string;
  updated_at?: string | null;
  finalized_at?: string | null;
  fill_route: string;
}

export interface DoctorWorkspaceResponse {
  summary: {
    patients: number;
    reports: number;
    drafts: number;
    final_reports: number;
  };
  patients: DoctorWorkspacePatient[];
  reports: DoctorWorkspaceReport[];
}

// =========================================================
// [API CALL]
// =========================================================
export const getClinicianSummary = async (): Promise<ClinicianHomeSummary> =>
  (await apiClient.get<ClinicianHomeSummary>("/clinician/home/summary")).data;

export const getDoctorWorkspace = async (params: {
  q?: string;
  status?: string;
  source?: "all" | "patients" | "reports";
}): Promise<DoctorWorkspaceResponse> =>
  (await apiClient.get<DoctorWorkspaceResponse>("/report-builder/doctor-workspace", { params })).data;

// =========================================================
// [HOOK 1] Page Visibility — ndalon polling kur tab mbyllur
// =========================================================
function usePageVisibility(): boolean {
  const [visible, setVisible] = useState(() =>
    typeof document !== "undefined" ? !document.hidden : true,
  );
  useEffect(() => {
    const h = (): void => setVisible(!document.hidden);
    document.addEventListener("visibilitychange", h);
    return () => document.removeEventListener("visibilitychange", h);
  }, []);
  return visible;
}

// =========================================================
// [HOOK 2] useClinicianSummary — adaptive polling
// Logjika:
//   - Tab i mbyllur    → polling ndalon (false)
//   - Data e njëjtë 3× → interval 30s → 60s (ngadalësohet)
//   - Data ndryshon    → kthehet në 30s
// =========================================================
export const useClinicianSummary = () => {
  const isVisible  = usePageVisibility();
  const [polling, setPolling] = useState<number>(30_000);
  const snapshots  = useRef<string[]>([]);

  const q = useQuery({
    queryKey:    ["clinician", "summary"],
    queryFn:     getClinicianSummary,
    staleTime:   15_000,
    refetchInterval: isVisible ? polling : false,
    refetchIntervalInBackground: false,
  });

  const snap = JSON.stringify(q.data ?? null);
  useEffect(() => {
    if (!q.data) return;
    snapshots.current = [...snapshots.current.slice(-2), snap];
    if (snapshots.current.length >= 3) {
      const stale = snapshots.current.every((s) => s === snapshots.current[0]);
      setPolling(stale ? 60_000 : 30_000);
    }
  }, [snap, q.data]);

  return q;
};

export const useDoctorWorkspace = (params: {
  q?: string;
  status?: string;
  source?: "all" | "patients" | "reports";
}, enabled = true) =>
  useQuery({
    queryKey: ["report-builder", "doctor-workspace", params],
    queryFn: () => getDoctorWorkspace(params),
    enabled,
    staleTime: 15_000,
  });

/**
 * =========================================================
 * Fund i professional/api.ts
 * =========================================================
 */
